import { useEffect, useState } from "react";
import { ApiError, api } from "../lib/api.js";
import { cancelCapture, finishCapture, readableTime, startCapture } from "../lib/capture.js";
import { enqueueCapture } from "../lib/offlineQueue.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import { Icon } from "../components/Icon.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { Panel } from "../components/Panel.js";
import { PhotoPicker, type PickedPhoto } from "../components/PhotoPicker.js";
import { useToast } from "../components/Toast.js";
import { ItemForm, draftToRequest, emptyDraft, type Draft } from "../components/ItemForm.js";

/**
 * The capture screen. Everything this stage is a bet about happens here.
 *
 * ## The loop
 *
 * Photograph, save, and immediately be ready for the next thing, **with the
 * location still filled in** -- because the next item is in the same bin as the
 * last one, and re-picking it fifty times is exactly the friction that makes
 * somebody stop cataloguing halfway through a shelf. "Save and add another" is
 * the primary button for that reason; the plain Save is for when the shelf is
 * done.
 *
 * ## Why the photos are already resized by the time Save is pressed
 *
 * `PhotoPicker` prepares each file the moment it is chosen, while somebody is
 * still deciding what to type. Saving is then a small POST and one upload per
 * photo, and the clock the gate cares about -- tap to saved -- has already had
 * the slow part taken out of it.
 *
 * ## The layout
 *
 * Photos on the left and words on the right on a desktop, stacked on a phone,
 * with the two save buttons in a bar pinned to the bottom of the screen: the
 * button a sitting presses fifty times never scrolls out of reach.
 *
 * ## The clock
 *
 * It starts when this screen opens, which is the tap, and stops when the last
 * photo has landed. See `lib/capture.ts` for why that is measured at all: a
 * gate written as a number and never measured is a gate that gets waved
 * through.
 *
 * ## No connection
 *
 * A basement is where signal goes to die. `save()` treats a dropped
 * connection (`ApiError.status === 0`) as its own outcome rather than an
 * error: whatever did not reach the worker is handed to
 * `lib/offlineQueue.ts`, which sends it once the connection is back, and the
 * loop continues exactly as if it had saved -- because from here, it has.
 */

export function Capture({
  taxonomy,
  onSaved,
  onDone,
  onStorage,
}: {
  taxonomy: Taxonomy;
  /** A saved item, for whatever wants to open it. */
  onSaved: (id: string) => void;
  /** Leaving the flow entirely. */
  onDone: () => void;
  /** The account's storage after an upload, so the shell can keep saying the truth. */
  onStorage: (usedBytes: number) => void;
}) {
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [photos, setPhotos] = useState<PickedPhoto[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ count: number; last: string | null }>({ count: 0, last: null });
  const toast = useToast();

  useEffect(() => {
    startCapture();
    // An abandoned capture must not leave a running clock for the next one to
    // finish, which would record a duration of "however long you had lunch".
    return cancelCapture;
  }, []);

  /** The bookkeeping every "this item is done" exit shares, saved or queued. */
  function afterSave(photoCount: number) {
    const timed = finishCapture(photoCount);
    taxonomy.remember(draft.locationId, draft.categoryId);
    for (const photo of photos) URL.revokeObjectURL(photo.previewUrl);
    setSaved((current) => ({ count: current.count + 1, last: timed ? readableTime(timed.ms) : null }));
    setPhotos([]);
  }

  /** The location and the category carry over; everything else does not. Carrying a title would be worse than useless -- it would be wrong on the next item and nobody would notice until much later. */
  function resetForNext() {
    setDraft({ ...emptyDraft(), locationId: draft.locationId, categoryId: draft.categoryId });
    startCapture();
  }

  /**
   * Create the item, then send the photos one at a time.
   *
   * Sequential rather than all at once on purpose: a phone on a basement's
   * worth of wifi does not go faster for being asked for four uploads at once,
   * the progress line can say something true, and the order photos arrive in is
   * the order they were taken -- which is what decides the cover.
   */
  async function save(another: boolean) {
    setError(null);
    setSaving(photos.length > 0 ? `Saving, 0 of ${photos.length} photos…` : "Saving…");
    const request = draftToRequest(draft);

    // Remembered across the try block so a dropped connection mid-upload can
    // still queue only what has not landed yet, rather than the whole item
    // over again -- see lib/offlineQueue.ts on why that matters.
    let itemId: string | null = null;
    let uploaded = 0;

    try {
      const { item } = await api.items.create(request);
      itemId = item.id;

      for (const photo of photos) {
        const { usedBytes } = await api.items.addPhoto(itemId, photo);
        onStorage(usedBytes);
        uploaded += 1;
        setSaving(`Saving, ${uploaded} of ${photos.length} photos…`);
      }

      afterSave(photos.length);
      if (another) {
        resetForNext();
      } else {
        onSaved(itemId);
      }
    } catch (problem) {
      if (problem instanceof ApiError && problem.status === 0) {
        await enqueueCapture(
          request,
          photos.slice(uploaded).map(({ full, thumb, width, height }) => ({ full, thumb, width, height })),
          itemId,
        );
        afterSave(photos.length);
        resetForNext();
        toast("Saved. No connection right now, so it'll upload on its own once you're back online.");
        return;
      }
      setError(
        problem instanceof ApiError
          ? problem.message
          : "That did not save. Nothing has been lost, so try again.",
      );
    } finally {
      setSaving(null);
    }
  }

  const busy = saving !== null;
  const nothingToSave = photos.length === 0 && draft.title.trim() === "";

  return (
    <div className="capture">
      <PageHeader
        title="New item"
        meta={
          saved.count > 0 ? (
            <span className="pill pill-ok">
              <Icon name="check" size={13} />
              {saved.count} saved{saved.last ? `, last took ${saved.last}` : ""}
            </span>
          ) : undefined
        }
        actions={
          <button
            type="button"
            className="button button-ghost"
            aria-label="Close"
            disabled={busy}
            onClick={onDone}
          >
            <Icon name="x" size={18} />
            <span className="hide-sm">Close</span>
          </button>
        }
      />

      <div className="capture-grid">
        <PhotoPicker photos={photos} onChange={setPhotos} onError={setError} busy={busy} />
        <Panel>
          <ItemForm draft={draft} onChange={setDraft} taxonomy={taxonomy} />
        </Panel>
      </div>

      {error && <Notice tone="error">{error}</Notice>}

      <div className="action-bar" data-unsaved={nothingToSave ? undefined : true}>
        <p className="action-bar-status" aria-live="polite">
          {saving ?? (nothingToSave ? "Add a photo or a title." : "")}
        </p>
        <button
          type="button"
          className="button button-quiet"
          disabled={busy || nothingToSave}
          onClick={() => void save(false)}
        >
          <span className="hide-sm">Save and finish</span>
          <span className="show-sm">Save</span>
        </button>
        <button
          type="button"
          className="button"
          disabled={busy || nothingToSave}
          onClick={() => void save(true)}
        >
          <Icon name="plus" size={18} />
          <span className="hide-sm">Save and add another</span>
          <span className="show-sm">Save, add next</span>
        </button>
      </div>
    </div>
  );
}
